// "Mark requested" — the biller has asked the payer (by portal, 278, email, fax
// or phone, outside Atlas) and records that they did.
//
// Atlas does not send anything from here. The dialog writes requested_at, the
// kind, the channel and the payer's reference if one was given, which takes the
// authorization off the action list and into "Waiting on payer" until an outcome
// is recorded.

import React, { useRef, useState } from "react";

import {
  CHANNEL_LABELS,
  KIND_LABELS,
  REQUEST_CHANNELS,
  REQUEST_KINDS,
  ACTION_TO_KIND,
  markRequested,
} from "./api";

export default function MarkRequestedModal({
  practiceId,
  flag,
  onClose,
  onDone,
  onStale,
}) {
  const [kind, setKind] = useState(ACTION_TO_KIND[flag.action] || "renewal");
  // The route's channel, when the route has a real one. "none" is not a way to
  // ask, so it is not a default.
  const [channel, setChannel] = useState(
    flag.route && REQUEST_CHANNELS.includes(flag.route.channel)
      ? flag.route.channel
      : "",
  );
  const [ref, setRef] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // A ref, not the state: synchronous repeats all read the render-time `busy`.
  const busyRef = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busyRef.current) return;
    if (!channel) {
      setError("Pick how the request was made.");
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await markRequested(practiceId, flag.authId, { kind, channel, ref });
      onDone(
        `${flag.authNumber || "The authorization"} is marked requested and waits on the payer.`,
      );
    } catch (err) {
      // Inside the dialog: the page banner renders behind the scrim.
      setError(err.message || "Could not mark this authorization as requested.");
      // 409: someone else already marked it. The list behind the dialog is out
      // of date, so it reloads now rather than keeping the flag after Cancel.
      if (err.status === 409 && onStale) onStale();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={busy ? undefined : onClose}>
      <div
        className="auth-modal auth-modal-small"
        role="dialog"
        aria-label="Mark requested"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-modal-header">
          <h3>Mark requested</h3>
          <button
            type="button"
            className="auth-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="auth-modal-body">
            {error && (
              <div className="auth-message auth-message-error auth-message-inline">
                {error}
              </div>
            )}
            <p>
              {flag.authNumber || "This authorization"} for{" "}
              {flag.patientName || "this patient"}
              {flag.authority ? ` with ${flag.authority}` : ""}.
            </p>
            <div className="auth-field-row">
              <label className="auth-field">
                <span>Kind</span>
                <select
                  className="auth-input"
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                >
                  {REQUEST_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="auth-field">
                <span>Requested by</span>
                <select
                  className="auth-input"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {REQUEST_CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {CHANNEL_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="auth-field auth-field-full">
              <span>Payer reference</span>
              <input
                className="auth-input"
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                maxLength={200}
                placeholder="Optional — the case or tracking number the payer gave"
              />
            </label>
            <p className="auth-modal-note">
              Atlas sends nothing to the payer. This records that you asked, so
              the authorization moves to “Waiting on payer” until the answer is
              recorded.
            </p>
          </div>
          <div className="auth-modal-footer">
            <button
              type="button"
              className="auth-btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={busy}
            >
              {busy ? "Saving…" : "Mark requested"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
